import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Smartphone,
  Loader2,
  CheckCircle,
  ArrowRight,
  User,
  Clock,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// KEYNOTE 24: a user whose handset is damaged or lost may sign in from any other
// handset; the admin just needs to be told it happened.
const DeviceAlerts = () => {
  const { api } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAlerts = async () => {
    try {
      const { data } = await api.get('/device-alerts');
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load device alerts:', error);
      toast.error('Failed to load device alerts');
    } finally {
      setLoading(false);
    }
  };

  const acknowledge = async (id) => {
    setProcessingId(id);
    try {
      await api.put(`/device-alerts/${id}/acknowledge`);
      toast.success('Alert acknowledged');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDateTime = (iso) =>
    new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  const pending = alerts.filter((a) => !a.acknowledged);

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="device-alerts-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Device Alerts</h1>
          <p className="text-sm text-slate-500">
            Raised when a user punches in from a different handset
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Unacknowledged</span>
          </div>
          <p className="text-3xl font-bold text-amber-700">{pending.length}</p>
        </div>

        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
              <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
              <p className="text-slate-500">No device changes reported</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  'bg-white rounded-2xl border p-4',
                  alert.acknowledged ? 'border-slate-100 opacity-70' : 'border-amber-200'
                )}
                data-testid={`device-alert-${alert.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-medium text-slate-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      {alert.user_name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
                      <code className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">
                        {alert.previous_device_id}
                      </code>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <code className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">
                        {alert.new_device_id}
                      </code>
                    </div>
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-2">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(alert.created_at)}
                    </p>
                  </div>
                  {alert.acknowledged ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">
                      Acknowledged
                    </span>
                  ) : (
                    <Button
                      onClick={() => acknowledge(alert.id)}
                      disabled={processingId === alert.id}
                      className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                      data-testid={`ack-${alert.id}`}
                    >
                      {processingId === alert.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Acknowledge
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DeviceAlerts;
